import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from '../shared/modal/modal.component';

// Result of a submitted counter (mock B2 → design doc
// docs/connect/2026-08-29-post-visit-completion-and-counter-offer-design.html).
// `counterNote` is already trimmed; both optional fields come back `null`
// rather than empty string so a caller can drop them from the request body
// the same way `null` is handled elsewhere (CounterOfferRequest on
// connect-service treats an absent value and a blank one identically).
export interface CounterOfferSubmission {
  counterFee: number;
  counterCompleteBy: string | null;
  counterNote: string | null;
}

/**
 * Counter-offer dialog (Task B4, mock B2). A pure form: it validates and emits
 * `(counterOffer)`, but does not call ProposalService itself — the host row
 * (ProductLineComponent) owns the API call and the list refresh, the same
 * split `decline-dialog` already uses for the OFFERED decline flow, so a
 * counter failure/rollback is handled exactly where the accept/decline
 * failures already are.
 *
 * Server contract (connect-service `CounterOfferRequest` /
 * `ProposalHandler.counterOwnedOffer`):
 *  - `counterFee` — required, positive, whole dollars.
 *  - `counterCompleteBy` — optional date.
 *  - `counterNote` — optional, capped at `maxNoteLength` (255) on the
 *    TRIMMED value (`ValidationUtils.MAX_NOTE_LENGTH`). The `maxlength`
 *    attribute below is a UX guard only; the length that matters is measured
 *    here on the trimmed string so a caller cannot pad past the count.
 *  - Legal only from OFFERED or RE_OFFERED — enforced server-side against the
 *    PERSISTED status; this dialog does not gate on status itself, the host
 *    row does (isOffered / isReOffered), so the affordance simply never
 *    exists for any other status. COUNTERED is NOT counterable: one counter
 *    per offer, and a standing counter is staffing's move to answer.
 *
 * The host still seeds `initialCounterFee` / `initialCounterCompleteBy` /
 * `initialCounterNote` from the persisted proposal, and this component
 * re-applies them every time it opens, so a second counter (from RE_OFFERED,
 * once staffing has answered) starts from the PA's previous ask rather than
 * blank.
 */
@Component({
  selector: 'counter-offer-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './counter-offer-dialog.component.html',
  styleUrl: './counter-offer-dialog.component.scss',
})
export class CounterOfferDialogComponent implements OnChanges {
  @Input() active: boolean = false;
  @Input() offeredFee: number = 0;
  @Input() siteType: string = '';
  @Input() siteSubType: string = '';
  @Input() address: any = { streetAddress: '', cityStateZip: '' };

  // Seed values for a revise — blank/null on a first-time counter.
  @Input() initialCounterFee: number | null = null;
  @Input() initialCounterCompleteBy: string | null = null;
  @Input() initialCounterNote: string = '';

  @Output() modalClosed = new EventEmitter<boolean>();
  @Output() counterOffer = new EventEmitter<CounterOfferSubmission>();

  // Server-enforced cap (ValidationUtils.MAX_NOTE_LENGTH), mirrored here as a
  // maxlength + counter the same way comments-card.component.ts does for its
  // 255-char note.
  readonly maxNoteLength = 255;

  counterFee: number | null = null;
  counterCompleteBy: string = '';
  counterNote: string = '';

  // Whether the PA has interacted with the fee field yet. Purely presentational:
  // it gates the error MESSAGE, never `feeInvalid` or `isValid`, so an empty
  // field is still refused by the submit guard whatever this says.
  //
  // It exists because `counterFee` starts null and `feeInvalid` is true for null,
  // so an ungated message rendered "Enter a counter price greater than $0" the
  // instant the dialog opened - an error on a pristine form, before the PA had
  // done anything wrong.
  feeTouched = false;

  // Re-seed the editable fields every time the dialog opens (`active` flips
  // false → true), so a revise picks up whatever the persisted proposal
  // carries right now rather than whatever was left typed the last time this
  // instance was open.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] && this.active) {
      this.counterFee = this.initialCounterFee ?? null;
      this.counterCompleteBy = this.initialCounterCompleteBy ?? '';
      this.counterNote = this.initialCounterNote ?? '';
      // Must reset with the rest, or a dialog closed while showing the error
      // re-opens still showing it against a field the PA has not touched yet.
      this.feeTouched = false;
    }
  }

  /**
   * Keeps the fee a whole number of dollars by making anything else untypeable.
   *
   * 🔴 The wire contract is a Java {@code Integer}: Jackson truncates `2150.75` to `2150` and answers
   * 200, so a PA would be bid at a price they never typed, on the one field where that matters most. A
   * submit-time check would still let them type it and see it; stripping on every input event means the
   * field can never hold it - which also covers paste, drag-drop and autofill, none of which a keydown
   * filter catches.
   *
   * Digits only, and the empty string maps to null rather than 0 so an emptied field reads as "not
   * answered" and {@link #feeInvalid} refuses it, rather than as a counter of zero.
   */
  onFeeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // 🔴 Split at the separator FIRST, then strip. Stripping non-digits alone turns a pasted "2150.75"
    // into 215075 - a 100x error, and far worse than the truncation this exists to prevent. Taking the
    // integer part makes the dangerous case safe: a paste lands 2150. Typing cannot produce a stray
    // separator anyway, because it is dropped the moment it is entered.
    // 🔴 Commas out FIRST, then split at the decimal point. The two separators are not interchangeable:
    // in "$2,150" the comma groups thousands, so splitting on it would land 2. Only "." ends the
    // integer part. Stripping non-digits without splitting is worse still - "2150.75" would concatenate
    // to 215075, a 100x error and the opposite of what this guard is for.
    const digits = input.value.replace(/,/g, '').split('.')[0].replace(/[^0-9]/g, '');
    // Written back unconditionally: when the strip removed something the DOM still holds the rejected
    // character, and only re-assigning clears it from what the PA sees.
    input.value = digits;
    this.counterFee = digits === '' ? null : Number(digits);
    this.feeTouched = true;
  }

  get feeInvalid(): boolean {
    return this.counterFee == null || this.counterFee <= 0;
  }

  /**
   * Whether to SHOW the fee error, as opposed to whether the fee is invalid.
   *
   * Deliberately separate from {@link #feeInvalid}: that one is the submit guard and must stay true
   * for an untouched empty field, or {@link #isValid} would let an empty counter through. This one
   * only decides whether the PA is told about it yet, and a pristine field has nothing to tell.
   */
  get feeError(): boolean {
    return this.feeTouched && this.feeInvalid;
  }

  get noteTooLong(): boolean {
    return this.counterNote.trim().length > this.maxNoteLength;
  }

  get isValid(): boolean {
    return !this.feeInvalid && !this.noteTooLong;
  }

  closeDialog(): void {
    this.active = false;
    this.modalClosed.emit(this.active);
  }

  submit(): void {
    if (!this.isValid) {
      return;
    }
    const trimmedNote = this.counterNote.trim();
    this.counterOffer.emit({
      counterFee: this.counterFee as number,
      counterCompleteBy: this.counterCompleteBy || null,
      counterNote: trimmedNote || null,
    });
    this.closeDialog();
  }
}
